import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { PermissionGate } from "@/components/common/PermissionGate"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import {
  isMsalAvailable,
  useErpMicrosoftAuth,
} from "@/hooks/useErpMicrosoftAuth"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconLoader2 } from "@tabler/icons-react"
import { Info, LogOut } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"
import { getApiErrorMessage } from "../../api/apiError"
import {
  useConnectErp,
  useDisconnectErp,
  useErpCatalog,
  useErpConnections,
  type ErpAuthMethod,
  type ErpCompany,
  type ErpSystemId,
} from "../../api/importsApi"
import { McPill } from "../shared/McPill"
import { ErpCredentialsDialog } from "./ErpCredentialsDialog"
import { ErpImportWizard } from "./ErpImportWizard"

// Display metadata (name + monogram + one-line description) for every ERP we show.
// Which are actually connectable — and how (Microsoft sign-in vs credentials form)
// — comes from the backend catalog; the rest render as "coming soon".
const ERP_DISPLAY: {
  id: ErpSystemId
  name: string
  monogram: string
  descKey: TranslationKeys
}[] = [
  {
    id: "business_central",
    name: "Microsoft Dynamics 365 Business Central",
    monogram: "BC",
    descKey: "MC_MD_ERP_DESC_BUSINESS_CENTRAL",
  },
  {
    id: "navision",
    name: "Microsoft Dynamics NAV (Navision)",
    monogram: "NAV",
    descKey: "MC_MD_ERP_DESC_NAVISION",
  },
  {
    id: "sap",
    name: "SAP S/4HANA",
    monogram: "SAP",
    descKey: "MC_MD_ERP_DESC_SAP",
  },
  {
    id: "proalpha",
    name: "proALPHA",
    monogram: "pA",
    descKey: "MC_MD_ERP_DESC_PROALPHA",
  },
  {
    id: "oxaion",
    name: "oxaion",
    monogram: "ox",
    descKey: "MC_MD_ERP_DESC_OXAION",
  },
]

export function ErpImportPanel() {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const { signIn, getToken } = useErpMicrosoftAuth()
  const { data: catalog = [] } = useErpCatalog()
  const { data: connections = [] } = useErpConnections()
  const { mutate: connectErp } = useConnectErp()
  const { mutate: disconnectErp } = useDisconnectErp()
  const [busyId, setBusyId] = useState<ErpSystemId | null>(null)
  const [wizardErp, setWizardErp] = useState<ErpSystemId | null>(null)
  const [credsErp, setCredsErp] = useState<ErpSystemId | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] =
    useState<ErpSystemId | null>(null)
  const [companySelect, setCompanySelect] = useState<{
    erpType: ErpSystemId
    tenantId: string
    companies: ErpCompany[]
  } | null>(null)

  async function handlePickCompany(companyId: string) {
    if (!companySelect) return
    const { erpType, tenantId } = companySelect
    try {
      const token = await getToken()
      connectErp(
        { erpType, body: { microsoftAccessToken: token, tenantId, companyId } },
        {
          onSuccess: () => setCompanySelect(null),
          onError: (e) =>
            toast.error(
              getApiErrorMessage(e) ?? translate("MC_MD_ERP_CONNECT_ERROR"),
            ),
        },
      )
    } catch (e) {
      toast.error(getApiErrorMessage(e) ?? translate("MC_MD_ERP_CONNECT_ERROR"))
    }
  }

  const connByType = new Map(connections.map((c) => [c.erpType, c]))
  const catalogByType = new Map(catalog.map((e) => [e.erpType, e]))

  async function handleMicrosoftConnect(id: ErpSystemId) {
    if (!isMsalAvailable) {
      toast.error(translate("MC_MD_ERP_MSAL_UNAVAILABLE"))
      return
    }
    setBusyId(id)
    try {
      const result = await signIn()
      const tenantId = result.account?.tenantId ?? ""
      connectErp(
        {
          erpType: id,
          body: { microsoftAccessToken: result.accessToken, tenantId },
        },
        {
          onSettled: () => setBusyId(null),
          onSuccess: (data) => {
            // Multi-company tenant → let the user pick before we bind one.
            // Discriminate on `companies` (both types carry `status`).
            if ("companies" in data) {
              setCompanySelect({
                erpType: id,
                tenantId,
                companies: data.companies,
              })
            }
          },
          onError: (e) =>
            toast.error(
              getApiErrorMessage(e) ?? translate("MC_MD_ERP_CONNECT_ERROR"),
            ),
        },
      )
    } catch (e) {
      setBusyId(null)
      // Closing the Microsoft popup rejects — only surface real failures.
      if (
        e instanceof Error &&
        !/user_cancelled|popup|user_cancel/i.test(e.message)
      ) {
        toast.error(
          getApiErrorMessage(e) ?? translate("MC_MD_ERP_CONNECT_ERROR"),
        )
      }
    }
  }

  // Credentials ERPs open a form; Microsoft ERPs run the sign-in popup.
  function handleConnect(id: ErpSystemId, authMethod: ErpAuthMethod) {
    if (authMethod === "credentials") {
      setCredsErp(id)
      return
    }
    void handleMicrosoftConnect(id)
  }

  function handleDisconnect(id: ErpSystemId) {
    disconnectErp(id, {
      onError: (e) =>
        toast.error(
          getApiErrorMessage(e) ?? translate("MC_MD_ERP_DISCONNECT_ERROR"),
        ),
    })
  }

  const wizardEntry = wizardErp ? catalogByType.get(wizardErp) : undefined
  const credsEntry = credsErp
    ? ERP_DISPLAY.find((e) => e.id === credsErp)
    : undefined

  return (
    <div className="rounded-xl border border-grey4 bg-grey2 p-6 md:min-h-[480px]">
      <p className="mb-6 max-w-xl text-sm text-grey5">
        {translate("MC_MD_ERP_INTRO")}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ERP_DISPLAY.map((sys) => {
          const conn = connByType.get(sys.id)
          const connected = !!conn
          const entry = catalogByType.get(sys.id)
          const available = !!entry
          const busy = busyId === sys.id
          return (
            <div
              key={sys.id}
              className={cn(
                "flex items-center gap-4 rounded-lg border border-grey4 bg-grey3/40 p-4 transition-colors",
                !available && !connected && "opacity-55",
              )}
            >
              <div
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold",
                  connected
                    ? "bg-success/10 text-success"
                    : "bg-grey4 text-grey5",
                )}
              >
                {sys.monogram}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {conn?.displayName ?? sys.name}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={translate(sys.descKey)}
                          className="shrink-0 text-grey5 transition-colors hover:text-white"
                        >
                          <Info size={13} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[240px] text-xs">
                        {translate(sys.descKey)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="mt-1.5">
                  <McPill tone={connected ? "success" : "muted"}>
                    {translate(
                      connected
                        ? "MC_MD_ERP_STATUS_CONNECTED"
                        : available
                          ? "MC_MD_ERP_STATUS_NOT_CONNECTED"
                          : "MC_MD_ERP_STATUS_SOON",
                    )}
                  </McPill>
                </div>
              </div>

              {connected ? (
                // Connect/disconnect/sync all require MC_IMPORTS_UPLOAD server-side
                // (api/v2/mc_imports.py) — a viewer without it would otherwise see
                // live buttons that only ever 403. A viewer still sees the status
                // pill above, just no actions.
                <PermissionGate permission={PERMISSIONS.MC_IMPORTS_UPLOAD}>
                  <div className="flex items-center gap-2">
                    {entry && !entry.supportsSync ? (
                      // Connect + preview both work here; only the full pull is
                      // unverified (oxaion's pagination) — sync_from_erp always
                      // 501s, so the button must say why it's off, not just be off.
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button size="sm" disabled>
                                {translate("MC_MD_ERP_IMPORT")}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[240px] text-xs">
                            {translate("MC_MD_ERP_SYNC_UNAVAILABLE")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setWizardErp(sys.id)}
                        // The wizard needs the catalog entry (for objects/auth);
                        // until the catalog query resolves, opening it would be
                        // a no-op.
                        disabled={!entry}
                      >
                        {translate("MC_MD_ERP_IMPORT")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-grey5"
                      onClick={() => setConfirmDisconnect(sys.id)}
                      aria-label={translate("MC_MD_ERP_DISCONNECT")}
                    >
                      <LogOut size={14} />
                    </Button>
                  </div>
                </PermissionGate>
              ) : available && entry ? (
                <PermissionGate permission={PERMISSIONS.MC_IMPORTS_UPLOAD}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleConnect(sys.id, entry.authMethod)}
                    disabled={busy}
                  >
                    {busy ? (
                      <IconLoader2 className="animate-spin" size={14} />
                    ) : null}
                    {translate(
                      busy
                        ? "MC_MD_ERP_CONNECTING"
                        : entry.authMethod === "credentials"
                          ? "MC_MD_ERP_CONNECT_CREDENTIALS"
                          : "MC_MD_ERP_CONNECT",
                    )}
                  </Button>
                </PermissionGate>
              ) : null}
            </div>
          )
        })}
      </div>

      {wizardErp && wizardEntry && (
        <ErpImportWizard
          open={!!wizardErp}
          onOpenChange={(o) => !o && setWizardErp(null)}
          erpType={wizardErp}
          authMethod={wizardEntry.authMethod}
          objects={wizardEntry.objects}
          getToken={getToken}
          onImported={(fileId) => {
            const params = new URLSearchParams([["file_ids", fileId]])
            navigate(`/material-compliance/master-data/processing?${params}`)
          }}
        />
      )}

      {credsErp && credsEntry && (
        <ErpCredentialsDialog
          open={!!credsErp}
          onOpenChange={(o) => !o && setCredsErp(null)}
          erpType={credsErp}
          erpName={credsEntry.name}
          authTypes={
            (catalogByType.get(credsErp)?.authTypes ?? []) as (
              | "apikey"
              | "basic"
              | "oauth"
            )[]
          }
        />
      )}

      {confirmDisconnect && (
        <ConfirmDialog
          title={translate("MC_MD_ERP_DISCONNECT_CONFIRM_TITLE")}
          message={translate("MC_MD_ERP_DISCONNECT_CONFIRM_MESSAGE")}
          confirmLabel={translate("MC_MD_ERP_DISCONNECT")}
          variant="danger"
          onConfirm={() => {
            handleDisconnect(confirmDisconnect)
            setConfirmDisconnect(null)
          }}
          onCancel={() => setConfirmDisconnect(null)}
        />
      )}

      {companySelect && (
        <Dialog open onOpenChange={(o) => !o && setCompanySelect(null)}>
          <DialogContent className="max-w-md gap-4">
            <DialogHeader>
              <DialogTitle>
                {translate("MC_MD_ERP_COMPANY_SELECT_TITLE")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              {companySelect.companies.map((c) => (
                <Button
                  key={c.id}
                  variant="outline"
                  className="justify-start"
                  onClick={() => handlePickCompany(c.id)}
                >
                  {c.name}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
